import { useState, useMemo } from 'react';
import { useRouter } from '@uirouter/react';
import moment from 'moment-timezone';

import { notifySuccess } from '@/portainer/services/notifications';
import { useEnvironmentList } from '@/react/portainer/environments/queries';
import type { Environment } from '@/react/portainer/environments/types';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { Button } from '@@/buttons';

import {
  PolicyConfigurationValues,
  PolicyConfigurationFormSection,
} from '../components/PolicyConfigurationFormSection';
import { PolicyTypeSelector } from '../components/PolicyTypeSelector/PolicyTypeSelector';
import {
  getEnvironmentTypeDisplayName,
  getPolicyTypeDisplayName,
  PolicyType,
  PolicyCategory,
} from '../types';
import { useCreatePolicyMutation } from '../queries/useCreatePolicyMutation';
import { CreatePolicyData } from '../queries/types';
import { useConfirmDistributePolicyModal } from '../components/useConfirmDistributePolicyModal';
import {
  getEnvironmentTypeFromPolicyType,
  getVisiblePolicyTypes,
  getSupportedEnvironmentTypes,
} from '../utils/policyTypeUtils';
import { KubernetesRBACPolicyForm } from '../components/kubernetes/rbac/KubernetesRBACPolicyForm';
import { useKubernetesRBACInitialValues } from '../components/kubernetes/rbac/useKubernetesRBACInitialValues';
import { buildKubernetesRBACPayload } from '../components/kubernetes/rbac/buildKubernetesRBACPayload';
import { KubernetesSecurityPolicyForm } from '../components/kubernetes/security/KubernetesSecurityPolicyForm';
import { useKubernetesSecurityInitialValues } from '../components/kubernetes/security/useKubernetesSecurityInitialValues';
import { buildKubernetesSecurityPayload } from '../components/kubernetes/security/buildKubernetesSecurityPayload';
import { KubernetesSetupPolicyForm } from '../components/kubernetes/setup/KubernetesSetupPolicyForm';
import { useKubernetesSetupInitialValues } from '../components/kubernetes/setup/useKubernetesSetupInitialValues';
import { buildKubernetesSetupPayload } from '../components/kubernetes/setup/buildKubernetesSetupPayload';
import { KubernetesRegistryPolicyForm } from '../components/kubernetes/registry/KubernetesRegistryPolicyForm';
import { useKubernetesRegistryInitialValues } from '../components/kubernetes/registry/useKubernetesRegistryInitialValues';
import { buildKubernetesRegistryPayload } from '../components/kubernetes/registry/buildKubernetesRegistryPayload';
import { DockerRBACPolicyForm } from '../components/docker/rbac/DockerRBACPolicyForm';
import { useDockerRBACInitialValues } from '../components/docker/rbac/useDockerRBACInitialValues';
import { buildDockerRBACPayload } from '../components/docker/rbac/buildDockerRBACPayload';
import { DockerSecurityPolicyForm } from '../components/docker/security/DockerSecurityPolicyForm';
import { useDockerSecurityInitialValues } from '../components/docker/security/useDockerSecurityInitialValues';
import { buildDockerSecurityPayload } from '../components/docker/security/buildDockerSecurityPayload';
import { DockerSetupPolicyForm } from '../components/docker/setup/DockerSetupPolicyForm';
import { useDockerSetupInitialValues } from '../components/docker/setup/useDockerSetupInitialValues';
import { buildDockerSetupPayload } from '../components/docker/setup/buildDockerSetupPayload';
import { DockerRegistryPolicyForm } from '../components/docker/registry/DockerRegistryPolicyForm';
import { useDockerRegistryInitialValues } from '../components/docker/registry/useDockerRegistryInitialValues';
import { buildDockerRegistryPayload } from '../components/docker/registry/buildDockerRegistryPayload';

type Step = 'select-type' | 'configure';

type EnvironmentTypeFilter =
  | 'all'
  | 'kubernetes'
  | 'docker'
  | 'podman'
  | 'swarm';
type CategoryFilter = 'all' | PolicyCategory;

export function PolicyCreateView() {
  const router = useRouter();
  const urlPolicyType = router.globals.params?.type as PolicyType | undefined;

  // Check if type is provided in URL - if so, skip step 1
  const initialStep: Step =
    urlPolicyType && isValidPolicyType(urlPolicyType)
      ? 'configure'
      : 'select-type';

  const [currentStep, setCurrentStep] = useState<Step>(initialStep);
  const [timeZone, setTimeZone] = useState<string>(moment.tz.guess());
  const [policyConfig, setPolicyConfig] = useState<PolicyConfigurationValues>({
    name: '',
    type:
      urlPolicyType && isValidPolicyType(urlPolicyType)
        ? urlPolicyType
        : 'rbac-k8s',
    environmentGroupIds: [],
  });

  // Filter states hoisted from PolicyTypeSelector
  const [environmentTypeFilter, setEnvironmentTypeFilter] =
    useState<EnvironmentTypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { environments } = useEnvironmentList();
  const groupsQuery = useGroups();
  const groups = useMemo(() => groupsQuery.data || [], [groupsQuery.data]);

  const createMutation = useCreatePolicyMutation();
  const confirmDistributePolicyModal = useConfirmDistributePolicyModal(
    policyConfig.name,
    policyConfig.environmentGroupIds
  );

  // Initialize all form hooks
  const k8sRbacInitialValues = useKubernetesRBACInitialValues();
  const k8sSecurityInitialValues = useKubernetesSecurityInitialValues();
  const k8sSetupInitialValues = useKubernetesSetupInitialValues();
  const k8sRegistryInitialValues = useKubernetesRegistryInitialValues();
  const dockerRbacInitialValues = useDockerRBACInitialValues();
  const dockerSecurityInitialValues = useDockerSecurityInitialValues();
  const dockerSetupInitialValues = useDockerSetupInitialValues();
  const dockerRegistryInitialValues = useDockerRegistryInitialValues();

  const derivedPolicyType = useDerivedPolicyType(
    environments,
    groups,
    environmentTypeFilter,
    categoryFilter,
    searchQuery,
    policyConfig.type
  );

  const environmentGroupsProvided = !!policyConfig.environmentGroupIds.length;
  const nameProvided = !!policyConfig.name.trim();

  let title = 'Create policy';
  if (currentStep === 'configure' && derivedPolicyType) {
    title = `Create ${getEnvironmentTypeDisplayName(
      getEnvironmentTypeFromPolicyType(derivedPolicyType)
    )} ${getPolicyTypeDisplayName(derivedPolicyType)} policy`;
  }

  return (
    <div className="form-horizontal">
      <PageHeader
        title={title}
        breadcrumbs={[{ label: 'Policies', link: 'portainer.policies' }, title]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody>
              <div className="form-group">
                <div className="col-sm-12">
                  {currentStep === 'select-type' && (
                    <PolicyTypeSelector
                      value={derivedPolicyType}
                      onChange={(type) =>
                        setPolicyConfig({ ...policyConfig, type })
                      }
                      onContinue={() => setCurrentStep('configure')}
                      environments={environments}
                      groups={groups}
                      environmentFilter={environmentTypeFilter}
                      onEnvironmentFilterChange={setEnvironmentTypeFilter}
                      categoryFilter={categoryFilter}
                      onCategoryFilterChange={setCategoryFilter}
                      searchQuery={searchQuery}
                      onSearchQueryChange={setSearchQuery}
                    />
                  )}

                  {currentStep === 'configure' && derivedPolicyType && (
                    <>
                      {/* Back button */}
                      <div className="mb-4">
                        <Button
                          color="default"
                          onClick={() => setCurrentStep('select-type')}
                          data-cy="back-to-policy-type-selection-button"
                        >
                          ← Back to Policy Type Selection
                        </Button>
                      </div>

                      {/* Policy configuration (name and groups) */}
                      <PolicyConfigurationFormSection
                        onChange={setPolicyConfig}
                        environmentType={getEnvironmentTypeFromPolicyType(
                          derivedPolicyType
                        )}
                        policyConfiguration={policyConfig}
                        disableCategory
                      />

                      {/* Render the appropriate policy form */}
                      {renderPolicyForm(
                        derivedPolicyType,
                        nameProvided,
                        environmentGroupsProvided
                      )}
                    </>
                  )}
                </div>
              </div>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </div>
  );

  function renderPolicyForm(
    policyType: PolicyType,
    nameProvided: boolean,
    environmentGroupsProvided: boolean
  ) {
    switch (policyType) {
      case 'rbac-k8s':
        return (
          <KubernetesRBACPolicyForm
            initialValues={k8sRbacInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildKubernetesRBACPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      case 'security-k8s':
        return (
          <KubernetesSecurityPolicyForm
            initialValues={k8sSecurityInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildKubernetesSecurityPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      case 'setup-k8s':
        return (
          <KubernetesSetupPolicyForm
            initialValues={k8sSetupInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildKubernetesSetupPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
            timeZone={timeZone}
            onChangeTimeZone={setTimeZone}
          />
        );

      case 'registry-k8s':
        return (
          <KubernetesRegistryPolicyForm
            initialValues={k8sRegistryInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildKubernetesRegistryPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      case 'rbac-docker':
        return (
          <DockerRBACPolicyForm
            initialValues={dockerRbacInitialValues}
            onSubmit={(values) => handleSubmit(values, buildDockerRBACPayload)}
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      case 'security-docker':
        return (
          <DockerSecurityPolicyForm
            initialValues={dockerSecurityInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildDockerSecurityPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      case 'setup-docker':
        return (
          <DockerSetupPolicyForm
            initialValues={dockerSetupInitialValues}
            onSubmit={(values) => handleSubmit(values, buildDockerSetupPayload)}
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
            timeZone={timeZone}
            onChangeTimeZone={setTimeZone}
          />
        );

      case 'registry-docker':
        return (
          <DockerRegistryPolicyForm
            initialValues={dockerRegistryInitialValues}
            onSubmit={(values) =>
              handleSubmit(values, buildDockerRegistryPayload)
            }
            nameProvided={nameProvided}
            environmentGroupsProvided={environmentGroupsProvided}
            mode="create"
          />
        );

      default:
        return null;
    }
  }

  async function handleSubmit<TFormData, TPayload extends CreatePolicyData>(
    formData: TFormData,
    payloadBuilder: (formData: TFormData) => TPayload
  ) {
    if (!policyConfig.name.trim() || !derivedPolicyType) {
      return;
    }

    const confirmed = await confirmDistributePolicyModal();
    if (!confirmed) {
      return;
    }

    await createMutation.mutateAsync(
      {
        Name: policyConfig.name,
        Type: derivedPolicyType,
        EnvironmentType: getEnvironmentTypeFromPolicyType(derivedPolicyType),
        EnvironmentGroups: policyConfig.environmentGroupIds,
        Data: payloadBuilder(formData),
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Policy created successfully');
          router.stateService.go('portainer.policies');
        },
      }
    );
  }
}

/**
 * Derives the appropriate policy type from server state and filters.
 *
 * Instead of syncing state with useEffect, we derive the valid selection from available options.
 * This avoids race conditions, extra renders, and preserves the original selection for restoration.
 *
 * Priority: current selection (if valid) → 'rbac-k8s' → first available option
 */
function useDerivedPolicyType(
  environments: Environment[],
  groups: EnvironmentGroup[],
  environmentFilter: EnvironmentTypeFilter,
  categoryFilter: CategoryFilter,
  searchQuery: string,
  currentPolicyType: PolicyType | null
): PolicyType | null {
  return useMemo(() => {
    const visiblePolicyTypes = getVisiblePolicyTypes(environments, groups);

    const filteredPolicyTypes = visiblePolicyTypes.filter((metadata) => {
      // Filter by environment type
      if (environmentFilter !== 'all') {
        const supportedEnvTypes = getSupportedEnvironmentTypes(metadata.id);
        if (!supportedEnvTypes.includes(environmentFilter)) {
          return false;
        }
      }

      // Filter by category
      if (categoryFilter !== 'all' && metadata.category !== categoryFilter) {
        return false;
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = metadata.displayName.toLowerCase().includes(query);
        const matchesDescription = metadata.description
          .toLowerCase()
          .includes(query);
        if (!matchesName && !matchesDescription) {
          return false;
        }
      }

      return true;
    });

    const availableOptions = filteredPolicyTypes.map((m) => m.id);

    // If current value exists in filtered options, keep it
    if (
      currentPolicyType &&
      availableOptions.includes(currentPolicyType as PolicyType)
    ) {
      return currentPolicyType;
    }

    // Try to default to k8s rbac if available
    if (availableOptions.includes('rbac-k8s')) {
      return 'rbac-k8s';
    }

    // Fall back to first available option
    return availableOptions[0] || null;
  }, [
    environments,
    groups,
    environmentFilter,
    categoryFilter,
    searchQuery,
    currentPolicyType,
  ]);
}

function isValidPolicyType(value: unknown): value is PolicyType {
  const validTypes: PolicyType[] = [
    'rbac-k8s',
    'security-k8s',
    'setup-k8s',
    'registry-k8s',
    'rbac-docker',
    'security-docker',
    'setup-docker',
    'registry-docker',
  ];
  return validTypes.includes(value as PolicyType);
}
